import { BfiApi, IApiResponse } from './bfiApi';
import { IBorrower } from './loanApi';
import { ISearchCriteria } from './common';
import { IAddress } from 'libs/store/src/cross';

export interface IRejectBrokerAgreementRequest {
    id: string;
    comment: string;
}

export interface IAddReferralRequest {
    brokerId: number;
    contactFirstName: string;
    contactMiddleName: string;
    contactLastName: string;
    contactSuffix: string;
    businessName: string;
    acceptedIcca: boolean;
    fontId: number;
}

export interface IUpdateBrokerAgreementEntity {
    brokerAgreementId: number;
    entityId: number;
}

export interface IBrokerAgreementSimilarNameCheck {
    businessName: string;
    personName: string;
    idToIgnore?: number;
}

export interface IBrokersListItem {
    id: number;
    companyName: string;
    personName: string;
}

export interface IBrokerItemResponse extends Omit<IApiResponse, 'result'> {
    result: IBrokersListItem;
}

export interface IBrokerAgreementListItem {
    id: number;
    brokerBusinessName: string;
    brokerContactName: string;
    brokerEmail: string;
    borrowerEntity: string;
    borrowerName: string;
    borrowerTin: string;
    borrowerEmail: string;
}

export interface IBrokerLean {
    id: number;
    businessName: string;
    fullName: string;
    email: string;
    phone: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface IBorrowerEntity {
    entityId: number;
    entityName: string;
    entityStateIncorporated: string;
    entityType: number;
    masterNoteId: number;
    address: IAddress;
}

export interface IBrokerAgreementSummary {
    broker: IBrokerLean;
    borrower: IBorrower;

    brokerAgreementId: number;
    entityId: number;
    borrowerEntities: IBorrowerEntity[];
}

export interface IBrokerAgreementsListItemResponse extends Omit<IApiResponse, 'result'> {
    result: [IBrokerAgreementListItem];
}

export interface IBrokerAgreementSummaryResponse extends Omit<IApiResponse, 'result'> {
    result: [IBrokerAgreementSummary];
}

export interface IReferralValidate {
    brokerId: number;
    borrowerEntity: string;
    contactFirstName: string;
    contactLastName: string;
}

export enum BorrowerValidateStatusType {
    OK = 0,
    EmailExists = 1,
    NameExists = 2,
    EntityExists = 3,
}

export interface IBorrowerValidateResult {
    Status: BorrowerValidateStatusType;
}

export interface IBorrowerValidateResponse extends Omit<IApiResponse, 'result'> {
    result: IBorrowerValidateResult;
}

export interface IDateHistory {
    dateTime: Date;
    event: string;
}

export interface IBClientPrincipalSummary {
    id: number;
    brokerPersonName: string;
    brokerBusinessName: string;
    brokerAddress: IAddress;
    history: [IDateHistory];
    iccaDownloadUrl: string;
    clientFeeType: string;
    status: string;
}

export interface IBClientPrincipalSummaryResponse {
    result: {
        totalCount: number;
        results: [IBClientPrincipalSummary];
    };
}

export class BrokerClientApi extends BfiApi {
    getBrokerClient(brokerClientId) {
        return super.get(`/brokerClient/get/${brokerClientId}`);
    }

    updateBrokerClient(obj: FormData) {
        return super.post('/brokerClient/update/', obj, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    acceptBrokerAgreement({ brokerAgreementId, logoUrl, fontId }) {
        return super.post('/brokerClient/acceptBrokerAgreement/', {
            brokerAgreementId,
            logoUrl,
            fontId,
        });
    }

    expireBrokerAgreement(id: number): Promise<IApiResponse> {
        return super.post('/brokerClient/expireBrokerAgreement', id);
    }

    rejectBrokerAgreement(rejection: IRejectBrokerAgreementRequest): Promise<IApiResponse> {
        return super.post('/brokerClient/rejectBrokerAgreement', rejection);
    }

    getPendingBrokerClientGridRows() {
        return super.post('/brokerClient/getByFilter/', { pending: 1 });
    }

    getExpireSoonBrokerClientGridRows() {
        return super.post('/brokerClient/getByFilter/', { expireSoon: 1 });
    }

    getActiveBrokerClientGridRows() {
        return super.post('/brokerClient/getByFilter/', { active: 1 });
    }

    getExpiredBrokerClientGridRows() {
        return super.post('/brokerClient/getByFilter/', { expired: 1 });
    }

    getRejectedBrokerClientGridRows() {
        return super.post('/brokerClient/getByFilter/', { rejected: 1 });
    }

    addReferral(referral: IAddReferralRequest): Promise<IApiResponse> {
        return super.post('/brokerClient/referrals/add', referral);
    }

    validateReferral(model: IReferralValidate): Promise<IBorrowerValidateResponse> {
        return super.post('/brokerClient/referrals/validate', model);
    }

    getPendingReferralsGridRows(brokerId) {
        return super.post('/brokerClient/getReferralsByFilter/', {
            brokerId: brokerId,
            pending: 1,
        });
    }

    getWaitingReferralsGridRows(brokerId) {
        return super.post('/brokerClient/getReferralsByFilter/', {
            brokerId: brokerId,
            waiting: 1,
        });
    }

    getAcceptedReferralsGridRows(brokerId) {
        return super.post('/brokerClient/getReferralsByFilter/', {
            brokerId: brokerId,
            accepted: 1,
        });
    }

    getIpAddress = async (): Promise<IApiResponse> => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', requestOptions);
        if (response.ok) {
            const resultText = await response.text();
            const resultJson = (resultText ?? '')
                .trim()
                .split('\n')
                .reduce(function (obj, pair) {
                    const tokens = pair.split('=');
                    return (obj[tokens[0]] = tokens[1]), obj;
                }, {});

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ip = (resultJson as any).ip;
            if (!ip) {
                this.logError('Failed to retrieve IP address! External service did not return IP.');
                return {
                    statusCode: response.status,
                    errors: [],
                    result: '',
                };
            }

            return {
                statusCode: response.status,
                errors: [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                result: ip,
            };
        } else {
            this.logError('Failed to retrieve IP address! External service did not return IP.');
            return {
                statusCode: response.status,
                errors: ['failed to retrieve user ip address'],
                result: '',
            };
        }
    };

    validateBrokerBorrowerCombination = (
        id: number,
        brokerId: number,
        principalId: number,
        entityId: number
    ): Promise<IApiResponse> => {
        return super.get(
            `/brokerClient/${id ?? 0}/validate?brokerId=${brokerId ?? 0}&principalId=${principalId ?? 0}&entityId=${entityId ?? 0}`
        );
    };

    createBrokerAgreementPdf(id: number): Promise<IBrokerItemResponse> {
        return super.post(`/brokerClient/${id}/createBrokerAgreementPdf`);
    }

    getPrincipalBClients(principalId: number, criteria: ISearchCriteria): Promise<IBClientPrincipalSummaryResponse> {
        return super.post(`/brokerClient/principals/${principalId ?? 0}`, criteria);
    }

    searchBrokerAgreements(searchText: string, pageNumber: number, pageSize: number): Promise<IBrokerAgreementsListItemResponse> {
        return super.get(
            `/brokerClient/search?searchText=${searchText ?? ''}&pageNumber=${pageNumber ?? 1}&pageSize=${pageSize ?? 50}`
        );
    }

    getBrokerAgreementSummary(id): Promise<IBrokerAgreementSummaryResponse> {
        return super.get(`/brokerClient/${id}/summary`);
    }

    updateBrokerAgreementEntity(data: IUpdateBrokerAgreementEntity): Promise<IBrokerAgreementSummaryResponse> {
        return super.post('/brokerClient/updateBrokerAgreementEntity', data);
    }
}
